import React, { useEffect, useState } from 'react';
import Slider from 'react-slick';
import { ComponentRendering, isEditorActive, Placeholder, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { documentService, windowService } from '../../utils/window.utils';
import { findCtaType } from '../../utils/popUpCtaType.utils';
import { isEmptyResponse } from '../../utils/checkEmptyResponse';
import { ProductTestimonialProps } from './types/productSeriesDetailTestimonial.types';
import { ProductTestimonialModel, ProductTestimonialSlideModel } from './models/productSeriesDetailTestimonial.model';
import useExperienceEditorPlaceholder from '../../hooks/useExperienceEditorPlaceholder';
import useMediaQuery from '../../hooks/useMediaQuery';
import CarouselArrow from './components/carouselArrow';
import CarouselDots from '../@shared/carouselDots';
import CarouselSlide from './components/carouselSlide';
import EmptyApiDataMessage from '../@shared/emptyApiDataMessage';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';
import Heading from '../@shared/heading';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import AppendDots from './components/appendDots';
import {
  descriptionKeySeriesTestimonials,
  eyebrowKeySeriesTestimonials,
  titleKeySeriesTestimonials,
} from '../../constants/dictionary.constants';
import './styles.scss';

const ProductSeriesDetailTestimonial = (props: ProductTestimonialProps): JSX.Element | null => {
  const isFieldsEmpty = props?.fields && Object.keys(props?.fields).length === 0;
  const apiResponse = !isFieldsEmpty && isEmptyResponse(props);
  const isEEActive = props?.sitecoreContext?.pageEditing;
  const isPlaceholderShown = useExperienceEditorPlaceholder(props);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [screenWidth, setScreenWidth] = useState(1680);
  const [activeCardIndex, updateActiveIndex] = useState(0);

  const popUpCtaType = findCtaType(props.rendering);
  const secondaryCTAClassName = popUpCtaType === 'primary-cta' ? 'testimonial__margin-left' : '';

  useEffect(() => {
    setScreenWidth(Math.min(windowService()?.innerWidth, 1680));
    if (!isDesktop && !isFieldsEmpty) {
      const width = 343 / (2 * ((props?.fields?.apiData?.innerComponents && props?.fields?.apiData?.innerComponents?.length) || 1));
      documentService().querySelector('body').style.setProperty('--slick-width', `${width}px`);
      documentService()
        .querySelector('body')
        .style.setProperty('--slick-width-active', `${width + 10}px`);
    }
  }, [props]); // eslint-disable-line

  // Component is unreleased
  if (props?.fields?.isComponentUnreleased) {
    return <UnreleasedComponent />;
  }

  //  star node opened directly in EE
  if (isPlaceholderShown) {
    return (
      <ExperienceEditorComponentPlaceholder image={props?.fields?.itemData?.thumbnail} componentName={props?.rendering?.componentName} />
    );
  }

  // not opened from star node
  // data missing when navigating via from products node
  if (!apiResponse && isEditorActive()) {
    return <EmptyApiDataMessage componentName={props?.rendering?.componentName} />;
  }

  // No data and not in EE
  if (!apiResponse && !isPlaceholderShown && !isEditorActive()) {
    return null;
  }

  if (isFieldsEmpty) {
    return null;
  }
  const model = new ProductTestimonialModel(props);
  const cardsCount = model.innerComponents.length;

  const containerPaddingLeft = 32;
  const largeImageWidth = isDesktop ? 664 : 300;
  const smallImageWidth = isDesktop ? 392 : 300;

  const totalElementsWidth =
    model.innerComponents?.reduce((sumWidths: number, curr: ProductTestimonialSlideModel) => {
      if (curr.cardStyles.value === 'Wide') {
        sumWidths += largeImageWidth;
      } else {
        sumWidths += smallImageWidth;
      }
      return sumWidths;
    }, 0) + containerPaddingLeft;
  const shouldInstanceCarousel = totalElementsWidth > screenWidth;
  const iconImage = isDesktop ? 'arrow-chevron-right' : 'arrow-simple-right';
  const showInfiniteCarouselWithArrows = !isEEActive && shouldInstanceCarousel;
  const settings = {
    infinite: showInfiniteCarouselWithArrows,
    dots: shouldInstanceCarousel,
    arrows: showInfiniteCarouselWithArrows,
    variableWidth: true,
    speed: 500,
    slidesToScroll: 1,
    nextArrow: <CarouselArrow direction="next" />,
    prevArrow: <CarouselArrow direction="previous" />,
    customPaging: CarouselDots,
    // eslint-disable-next-line react/display-name
    appendDots: (dots) => AppendDots({ cardsCount, activeCardIndex, dots }),
    afterChange: (index): void => {
      updateActiveIndex(index);
    },
  };

  return (
    <>
      {model.innerComponents.length > 0 ? (
        <div className="testimonial-wrapper">
          <div className="testimonial">
            <div className="testimonial__text-wrapper">
              <Heading
                editable={false}
                eyebrow={model.eyebrow}
                title={model.title}
                extraClassNameLink={secondaryCTAClassName}
                subtitle={model.description}
                link={{
                  value: {
                    href: model.sectionCtaSecondary?.value?.href,
                    text: model.sectionCtaSecondary?.value?.title,
                    target: model?.sectionCtaSecondary?.value?.target,
                  },
                }}
                isGenericPopup={!!props.rendering?.placeholders?.['jss-generic-popup']?.length}
                useDictionaryFallback
                eyebrowDictionaryKey={eyebrowKeySeriesTestimonials}
                titleDictionaryKey={titleKeySeriesTestimonials}
                descriptionDictionaryKey={descriptionKeySeriesTestimonials}
              >
                <div className={'testimonial__pop-up'}>
                  <Placeholder name={'jss-generic-popup'} rendering={props.rendering as ComponentRendering} />
                </div>
              </Heading>
            </div>

            <Slider {...settings} className="testimonial__cards">
              {model.innerComponents.map((item) => (
                <div key={item?.id} className="testimonial__slide">
                  <CarouselSlide model={item} isEE={isEEActive} iconImage={iconImage} isDesktop={isDesktop} />
                </div>
              ))}
            </Slider>
            <div className="testimonial__footer"></div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default withSitecoreContext()(ProductSeriesDetailTestimonial);
